import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPrice } from '../services/bookingService';
import { parseISO, format } from 'date-fns';

const Confirmation = ({ 
  bookingData, 
  clinic,
  onConfirm,
  loading = false
}) => {
  console.log('Confirmation component rendering');
  console.log('bookingData:', bookingData ? JSON.stringify(bookingData, null, 2) : 'null');
  console.log('clinic:', clinic ? JSON.stringify(clinic, null, 2) : 'null');
  const formatAppointmentTime = () => {
    console.log('Appointment time object keys:', Object.keys(bookingData?.appointmentTime || {}));
    
    // Try to use the best available formatted time in this order:
    // 1. time_display from the selected button (the exact time user clicked)
    // 2. formatted_adelaide from the API (correctly uses Adelaide timezone)
    // 3. formatted from the API (should use Australia/Adelaide timezone)
    // 4. timeString + date format (manually constructed from time selection)
    // 5. Raw ISO time as last resort (least reliable)
    
    // Check for time_display from button selection
    if (bookingData?.appointmentTime?.time_display) {
      console.log('Using time_display from selection:', bookingData.appointmentTime.time_display);
      return bookingData.appointmentTime.time_display;
    }
    
    // Check for Adelaide-formatted time
    if (bookingData?.appointmentTime?.formatted_adelaide) {
      console.log('Using formatted_adelaide time:', bookingData.appointmentTime.formatted_adelaide);
      return bookingData.appointmentTime.formatted_adelaide;
    }
    
    // Check for regular formatted time (which should now use Adelaide timezone)
    if (bookingData?.appointmentTime?.formatted) {
      console.log('Using formatted time from API:', bookingData.appointmentTime.formatted);
      return bookingData.appointmentTime.formatted;
    }
    
    // If no formatted times, check for timeString
    if (bookingData?.appointmentTime?.timeString && bookingData?.appointmentTime?.date) {
      try {
        const dayOfWeek = format(parseISO(bookingData.appointmentTime.date), 'EEEE');
        const monthAndDay = format(parseISO(bookingData.appointmentTime.date), 'MMMM d, yyyy');
        console.log('Using constructed time from timeString:', `${dayOfWeek}, ${monthAndDay} at ${bookingData.appointmentTime.timeString}`);
        return `${dayOfWeek}, ${monthAndDay} at ${bookingData.appointmentTime.timeString}`;
      } catch (err) {
        console.error('Error formatting from timeString:', err);
      }
    }
    
    // Last resort: use ISO time
    if (bookingData?.appointmentTime?.iso) {
      try {
        const startTimeISO = bookingData.appointmentTime.iso;
        console.log('Using raw ISO time (last resort):', startTimeISO);
        
        // Parse and format the ISO time
        const startTime = parseISO(startTimeISO);
        return format(startTime, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
      } catch (err) {
        console.error('Error formatting ISO time:', err);
      }
    }
    
    // If none of the above work, return unknown
    console.log('No valid time format found');
    return 'Unknown time';
  };
  
  // Check for post-payment scenario - if we're on payment-complete but have incomplete data
  const isPaymentCompletePath = window.location.href.includes('payment-complete');
  const hasSessionId = new URLSearchParams(window.location.search).get('session_id');

  // Ensure we have all necessary data
  if (!bookingData || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
    // If we're on payment-complete page, we should try to restore from sessionStorage
    if (isPaymentCompletePath || hasSessionId) {
      try {
        console.log('Missing booking data on payment completion page, checking sessionStorage...');
        const savedSession = sessionStorage.getItem('bookingSession');
        
        if (savedSession) {
          console.log('Found saved booking session, using for confirmation');
          // Instead of trying to modify the bookingData prop, we'll render alternative content
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Payment Successful
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                Your payment has been processed successfully. Your appointment has been booked.
              </Alert>
              
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" gutterBottom sx={{backgroundColor: 'primary.main', padding: '10px', color: 'white'}}>
                  🎉 Appointment Confirmed! 🎉
                </Typography>
                <Typography>
                  Your appointment has been booked successfully. You should receive a confirmation email shortly.
                </Typography>
                
                <Box sx={{ mt: 4 }}>
                  <Button 
                    variant="contained"
                    sx={{fontSize: '18px', padding: '15px'}}
                    color="primary"
                    onClick={() => {
                      // Attempt to return to clinic home
                      const pathParts = window.location.pathname.split('/');
                      const clinicStub = pathParts[1]; // The first segment after the domain
                      window.location.href = `/${clinicStub || ''}`;
                    }}
                  >
                    Return to Home
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        }
      } catch (e) {
        console.error('Error recovering booking data from sessionStorage:', e);
      }
    }
    
    return (
      <Alert severity="error">
        Booking information is incomplete. Please go back and complete all required steps.
      </Alert>
    );
  }
  
  // Check if this is a mobile browser
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  console.log('Browser detection - Mobile:', isMobile);
  
  // On payment-complete path, always consider payment as completed
  if (window.location.pathname.includes('payment-complete')) {
    console.log('URL includes payment-complete path, payment is considered complete');
    
    // Force payment status to complete if not already
    if (!bookingData.payment || bookingData.payment.status !== 'complete') {
      console.log('Setting payment status to complete based on URL path');
      
      // Get any transaction ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const transactionId = urlParams.get('id') || urlParams.get('transaction_id') || sessionId || 'stripe-payment';
      
      // Modify bookingData directly (not ideal but necessary for this case)
      bookingData.payment = {
        status: 'complete',
        transactionId: transactionId
      };
    }
  }
  // For other paths, check payment status
  else if (!bookingData.payment || bookingData.payment.status !== 'complete') {
    // Check URL parameters for payment completion evidence
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionId = urlParams.get('session_id');
    const hasSuccess = urlParams.get('success') === 'true';
    
    // If we have a session ID and success flag in the URL, assume payment is complete
    if (hasSessionId && hasSuccess) {
      console.log('Found Stripe session ID and success flag in URL, assuming payment is complete');
      // Set payment status to complete
      bookingData.payment = {
        status: 'complete',
        transactionId: hasSessionId
      };
    } else {
      console.log('Missing payment status in booking data, checking URL params for evidence');
      
      // Check if any other URL parameters indicate payment completion
      const hasTransactionId = urlParams.get('id') || urlParams.get('transaction_id');
      const hasAccessCode = urlParams.get('AccessCode');
      
      if (hasTransactionId || hasAccessCode) {
        console.log('Found payment evidence in URL parameters:', { hasTransactionId, hasAccessCode });
        // Set payment status to complete
        bookingData.payment = {
          status: 'complete',
          transactionId: hasTransactionId || 'payment-complete'
        };
      } else {
        console.log('No payment evidence found in booking data or URL');
        return (
          <Alert severity="warning">
            Payment must be completed before confirming booking.
          </Alert>
        );
      }
    }
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Your Booking
      </Typography>
      
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="success.main">
                  {bookingData.service.price === 0 || bookingData.service.price === null ? 'Free Appointment' : 'Payment Received'}
                </Typography>
              </Box>
              {bookingData.payment && bookingData.payment.transactionId && (
                <Typography variant="body2" color="text.secondary">
                  Transaction ID: {bookingData.payment.transactionId}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Clinic
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {clinic?.name || 'Unknown Clinic'}
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Service
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.service.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.service.duration_in_minutes} minutes
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Practitioner
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.practitioner.name}
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {formatAppointmentTime()}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Patient
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.patient.first_name} {bookingData.patient.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.patient.email}
              </Typography>
              {bookingData.patient.phone && (
                <Typography variant="body2" color="text.secondary">
                  {bookingData.patient.phone}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {bookingData.notes && (
              <>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {bookingData.notes}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
              </>
            )}
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total Paid
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6" color="primary">
                {(() => {
                  // Comprehensive check for free appointment price
                  const servicePrice = bookingData.service.price;
                  
                  // Convert to number if string
                  let numericPrice = servicePrice;
                  if (typeof servicePrice === 'string') {
                    numericPrice = parseFloat(servicePrice);
                    if (isNaN(numericPrice)) {
                      numericPrice = servicePrice;
                    }
                  }
                  
                  // Check if it's free using multiple approaches
                  const isFree = 
                    servicePrice === null || 
                    servicePrice === 0 || 
                    servicePrice === '0' || 
                    numericPrice === 0 ||
                    numericPrice === 0.0;
                    
                  return isFree ? 'Free' : formatPrice(bookingData.service.price);
                })()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        {/* Check if booking is already confirmed via appointment_created flag */}
        {bookingData.payment && bookingData.payment.appointment_created ? (
          <Box>
            <Typography variant="body1" paragraph>
              Your appointment has been booked successfully! An email confirmation has been sent to you.
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                // Return to clinic home
                const pathParts = window.location.pathname.split('/');
                const clinicStub = pathParts[1]; // The first segment after the domain
                window.location.href = `/${clinicStub || ''}`;
              }}
            >
              Return to Home
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" paragraph>
              Please confirm your booking to finalize your appointment. An email confirmation will be sent to you.
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                // Call the confirmation function
                onConfirm();
                
                // For mobile browsers, show a success message immediately
                if (isMobile) {
                  // Set a short timeout to allow the server request to be sent first
                  setTimeout(() => {
                    // Show success message for mobile browsers
                    const successBox = document.createElement('div');
                    successBox.style.position = 'fixed';
                    successBox.style.top = '0';
                    successBox.style.left = '0';
                    successBox.style.width = '100%';
                    successBox.style.height = '100%';
                    successBox.style.backgroundColor = 'white';
                    successBox.style.zIndex = '9999';
                    successBox.style.padding = '20px';
                    successBox.style.display = 'flex';
                    successBox.style.flexDirection = 'column';
                    successBox.style.alignItems = 'center';
                    successBox.style.justifyContent = 'center';
                    
                    // Create success message
                    const messageDiv = document.createElement('div');
                    messageDiv.style.backgroundColor = '#2e7d32'; // Success green
                    messageDiv.style.color = 'white';
                    messageDiv.style.padding = '10px';
                    messageDiv.style.borderRadius = '4px';
                    messageDiv.style.marginBottom = '20px';
                    messageDiv.style.textAlign = 'center';
                    messageDiv.style.width = '100%';
                    messageDiv.innerHTML = '🎉 Appointment Confirmed! 🎉';
                    
                    // Create text message
                    const textDiv = document.createElement('div');
                    textDiv.style.marginBottom = '30px';
                    textDiv.style.textAlign = 'center';
                    textDiv.innerHTML = 'Your appointment has been booked successfully. You should receive a confirmation email shortly.';
                    
                    // Create home button
                    const homeButton = document.createElement('button');
                    homeButton.style.backgroundColor = '#1976d2'; // Primary blue
                    homeButton.style.color = 'white';
                    homeButton.style.border = 'none';
                    homeButton.style.borderRadius = '4px';
                    homeButton.style.padding = '15px';
                    homeButton.style.fontSize = '18px';
                    homeButton.style.cursor = 'pointer';
                    homeButton.innerHTML = 'Return to Home';
                    homeButton.onclick = () => {
                      // Return to clinic home
                      const pathParts = window.location.pathname.split('/');
                      const clinicStub = pathParts[1]; // The first segment after the domain
                      window.location.href = `/${clinicStub || ''}`;
                    };
                    
                    // Add all elements to the success box
                    successBox.appendChild(messageDiv);
                    successBox.appendChild(textDiv);
                    successBox.appendChild(homeButton);
                    
                    // Add to document body
                    document.body.appendChild(successBox);
                  }, 300);
                }
              }}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Processing...' : 'Confirm Booking'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Confirmation;